import styled from "@emotion/styled"
import * as uniform from "@uniformdev/context-react"
import { Icon } from "components/icon/Icon"
import Configure from "components/nav-v2/Configure"
import { cssVars } from "components/nav-v2/Wrapper"
import { useConsole } from "contexts/Console"
import { useNavigation } from "contexts/Navigation"
import getMediaQuery from "css/breakpoints"
import { buttonFilled, opaqueGreen } from "css/buttons"
import { bold, legend100 } from "css/text"
import { useMotionValue, useMotionValueEvent } from "framer-motion"
import { useLayoutEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

/**{config_launcher ? <Configure {...config_launcher} /> : null} */

const Root = styled.div`
  --hide: 0;
  position: fixed;
  z-index: 9990;
  height: auto;
  top: calc((var(${cssVars.main.safe}) * var(${cssVars.main.bar.pinned}) + 1.38rem) - 100% * max(0, var(${cssVars.main.lock})));
  inset-inline-end: var(--outer-margin);
  box-shadow: 0 4px 28px 0 rgba(0, 0, 0, 0.1);
  transform: translate3d(0, calc(var(--hide) * -100% - var(--hide) * 1.38rem), 0);
  transition: transform 400ms;

  & > :is(a, button) {
    ${buttonFilled}
    ${opaqueGreen}
    ${legend100}
    line-height: 1rem;
    transition: background 300ms;
    height: unset;

    & * {
      pointer-events: none;
    }

    display: flex !important;
    justify-content: center;
    align-items: center;
    white-space: nowrap !important;
    padding-block: 0.5rem !important;
    padding-inline: 0.88rem !important;
    align-items: center;
    border-radius: 0.375rem;

    & > svg {
      margin-inline: var(--gap) 0 !important;
    }

    html:not([dir="rtl"]) & svg {
      // transform: scaleX(-1);
    }

    & > span.link-content {
      ${legend100}
      ${bold}
      clip-path: initial;
      height: initial;
      overflow: initial;
      position: initial;
      white-space: nowrap;
      width: initial;
      display: none;
    }

    ${getMediaQuery("m")} {
      & > span.link-content {
        display: initial;
      }
    }
  }
`

const Tools = ({ children, ...props }) => {
  const console = useConsole()
  const navigation = useNavigation()
  const location = useLocation()
  const scores = uniform.useScores()
  const rroot = useRef(null)

  const fromTestimonees = Boolean(
    location?.state?.fromTestimonees ||
      (typeof window !== "undefined" && window.history?.state?.state?.fromTestimonees) ||
      (typeof window !== "undefined" && window.history?.state?.fromTestimonees)
  )

  const fromScrollingGrid = Boolean(
    location?.state?.fromScrollingGrid ||
      (typeof window !== "undefined" && window.history?.state?.state?.fromScrollingGrid) ||
      (typeof window !== "undefined" && window.history?.state?.fromScrollingGrid)
  )

  const isFromTestimonees = !navigation.isFirstNavigation && fromTestimonees
  const isFromScrollingGrid = !navigation.isFirstNavigation && fromScrollingGrid

  const hide = useMotionValue(0)
  useMotionValueEvent(hide, "change", latest => {
    if (rroot.current) {
      rroot.current.style.setProperty("--hide", latest)
    }
  })

  const chooseVariant = p13n => {
    const variants = (p13n?.variants ?? [])
      .map(variant => variant.variant ?? variant)
      .filter(Boolean)
      .sort((a, b) => {
        if (!Array.isArray(a.criteria?.name?.crit) && Array.isArray(b.criteria?.name?.crit)) return 1
        if (Array.isArray(a.criteria?.name?.crit) && !Array.isArray(b.criteria?.name?.crit)) return -1
        return 0
      })

    for (const variant of variants) {
      if (!variant.criteria?.name?.crit) {
        return variant
      }

      const hit = variant.criteria.name.crit.every(crit => {
        const { l: signal, op: operator, r: value } = crit

        switch (operator) {
          case "=":
            return +scores[signal] === +value
          default:
            return false
        }
      })

      if (hit) {
        return variant
      }
    }

    return null
  }

  const hasLinkContent = link => link?.href && link?.label
  const propEntries = Object.values(props).filter(item => item && typeof item === "object")

  const backLinksP13n = propEntries.filter(item => item.backlinkp13n).map(item => chooseVariant(item.backlinkp13n))
  const backLinks = backLinksP13n.length ? backLinksP13n.filter(hasLinkContent) : propEntries.map(item => item.backlink).filter(hasLinkContent)

  const isTestimoneesBackLink = link => {
    if (!link) return false
    const href = (link.href || "").split("?")[0].split("#")[0].replace(/\/$/, "")
    const normalized = href.startsWith("/") ? href : "/" + href
    if (normalized === "/rolex-family" || normalized.endsWith("/rolex-family") || href.includes("rolex-family")) {
      return true
    }
    if (link.label && typeof link.label === "string" && link.label.toLowerCase().includes("rolex family")) {
      return true
    }
    return false
  }

  const isScrollingGridBackLink = link => {
    if (!link) return false
    const href = (link.href || "").split("?")[0].split("#")[0].replace(/\/$/, "")
    const normalized = href.startsWith("/") ? href : "/" + href
    if (
      normalized === "/watchmaking/features" ||
      normalized.endsWith("/watchmaking/features") ||
      href.includes("watchmaking/features")
    ) {
      return true
    }
    if (link.label && typeof link.label === "string" && link.label.toLowerCase().includes("watchmaking features")) {
      return true
    }
    return false
  }

  const isBackToPrevUrl = link => {
    const prevUrl =
      location?.state?.prevUrl ||
      (typeof window !== "undefined" && window.history?.state?.state?.prevUrl) ||
      (typeof window !== "undefined" && window.history?.state?.prevUrl)
    if (!prevUrl || !link?.href) return false
    try {
      const prevPath = new URL(prevUrl, window.location.origin).pathname.replace(/\/$/, "")
      const linkPath = new URL(navigation.localize(link.href), window.location.origin).pathname.replace(/\/$/, "")
      return prevPath === linkPath
    } catch {
      return false
    }
  }

  const shouldHideLink = link => {
    if (isFromTestimonees && (isTestimoneesBackLink(link) || isBackToPrevUrl(link))) {
      return true
    }
    if (isFromScrollingGrid && (isScrollingGridBackLink(link) || isBackToPrevUrl(link))) {
      return true
    }
    return false
  }

  const visibleBackLinks = backLinks.filter(link => !shouldHideLink(link))

  const externalLinksP13n = propEntries.filter(item => item.externallinkp13n).map(item => chooseVariant(item.externallinkp13n))
  const externalLinks = externalLinksP13n.length ? externalLinksP13n.filter(hasLinkContent) : propEntries.map(item => item.externallink).filter(hasLinkContent)

  const configurator = propEntries.filter(item => item.configurator).map(item => item.configurator)

  const onClick = e => {
    e.preventDefault()
    navigation.navigate(e.currentTarget.getAttribute("href"))
  }

  useLayoutEffect(() => {
    const controller = new AbortController()
    const root = rroot.current
    const footer = document.querySelector("footer .sections")
    let intersectionObserver = null

    if (footer && root) {
      const onresize = () => {
        const innerHeight = window.innerHeight
        const nodeHeight = root.clientHeight
        const headerSafe = getComputedStyle(document.documentElement)?.getPropertyValue(cssVars.main.nsafe)

        const ioMargin = innerHeight - headerSafe - nodeHeight * 2

        if (intersectionObserver) intersectionObserver.disconnect()
        intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            hide.set(+entry.isIntersecting)
          },
          { rootMargin: `0px 0px ${-ioMargin}px 0px` }
        )
        intersectionObserver.observe(footer)
      }
      global.addEventListener("resize", onresize, { signal: controller.signal })
      onresize()
    }

    return () => {
      controller.abort()
      if (intersectionObserver) intersectionObserver.disconnect()
    }
  })

  console.verbose("tools(%o)", "props", props, "other", { backLinks: visibleBackLinks, externalLinks, configurator })
  if (configurator.length) {
    return <Configure {...configurator[0]} />
  }

  if (!visibleBackLinks.length && !externalLinks.length) {
    return null
  }

  return (
    <Root ref={rroot}>
      {visibleBackLinks
        ? visibleBackLinks.map(link => (
            <a className='inline' key={link.href} href={navigation.localize(link.href)} onClick={onClick} aria-label={link.aria_label}>
              {link.label}
            </a>
          ))
        : null}

      {externalLinks
        ? externalLinks.map(link => (
            <a className='inline' key={link.href} href={link.href} target='_blank' rel='noopener noreferrer' aria-label={link.aria_label}>
              {link.label}
              <Icon type='externalLink' fill='currentColor' />
            </a>
          ))
        : null}
    </Root>
  )
}

export default Tools
